import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Row,
  Col,
  Select,
  Input,
  Button,
  Checkbox,
  Collapse,
  message,
} from "antd";
import PropTypes from "prop-types";
import decryptedModulesData from "../../../utils/decryptedModules";

const { Panel } = Collapse;

const UserModal = ({
  fromTitle,
  open,
  buttonLoading,
  handleCancel,
  handleSubmit,
  editFlag,
  formButton,
  UserForm,
  selectedModules,
  setSelectedModules,
  handleModuleChangenew,
  roles,
  modules,
  loggedInUser,
}) => {
  const [permissions, setPermissions] = useState({});
  const [messageApi, contextHolder] = message.useMessage();

  // first need to get user module and then all modules and show filtered modules to create new user

  const userModules = decryptedModulesData().modules;
  const allModules = modules;
  const filteredModules = allModules.filter(
    (module) =>
      Array.isArray(userModules) &&
      userModules.some(
        (userModule) =>
          userModule.module.toLowerCase() === module.moduleName.toLowerCase()
      )
  );

  // Show error message
  const showErrorMessage = (error) => {
    messageApi.error(error);
  };

  // Handle individual module permissions
  const handlePermissionChange = (module, selectedPermissions) => {
    if (
      selectedPermissions.length > 0 &&
      !selectedPermissions.includes("read")
    ) {
      selectedPermissions.push("read");
    }
    setPermissions((prev) => ({
      ...prev,
      [module]: selectedPermissions,
    }));
    UserForm.setFieldsValue({ [`${module}_permissions`]: selectedPermissions });
  };

  const onChangeRolePermission = (value) => {
    // Reset selected modules and set role in the form
    setSelectedModules([]);
    UserForm.setFieldsValue({ role: value });

    const selectedRole = roles.find((r) => r.roleId === value);
    const roleName = selectedRole?.role?.toLowerCase();
    if (!roleName) return;

    if (roleName === "super admin" || roleName === "master admin") {
      // Map filtered modules to {module, key, moduleId}
      const filteredModulesForRole = filteredModules.map((m) => ({
        module: m.moduleName,
        key: m.key,
        moduleId: m._id,
      }));

      // Create default full permissions
      const newPermissions = {};
      filteredModulesForRole.forEach((m) => {
        newPermissions[m.module] = ["read", "create", "update", "delete"];
      });

      // Set permissions state
      setPermissions(newPermissions);

      // Set form values
      const formValues = {
        permissions: filteredModulesForRole.map((m) => m.module),
      };
      filteredModulesForRole.forEach((m) => {
        formValues[`${m.module.replace(/\s+/g, "_")}_permissions`] =
          newPermissions[m.module];
      });

      UserForm.setFieldsValue(formValues);
      // Update selectedModules state
      setSelectedModules(filteredModulesForRole);
    } else {
      // Clear permissions for other roles
      setPermissions({});
      UserForm.setFieldsValue({
        permissions: [],
        ...Object.fromEntries(
          modules?.map((m) => [
            `${m.moduleName.replace(/\s+/g, "_")}_permissions`,
            undefined,
          ])
        ),
      });
      setSelectedModules([]);
    }
  };

  useEffect(() => {
    if (open && editFlag) {
      // Initialize permissions state from the form values when editing
      const initialPerms = {};
      selectedModules.forEach((m) => {
        const formValue = UserForm.getFieldValue(
          `${m.module.replace(/\s+/g, "_")}_permissions`
        );
        initialPerms[m.module] = formValue || [];
      });
      setPermissions(initialPerms);
    } else if (open && !editFlag) {
      // Reset permissions for new user
      setPermissions({});
    }
  }, [open, editFlag, selectedModules, UserForm]);

  return (
    <>
      {contextHolder}
      <Modal
        title={fromTitle}
        open={open}
        onCancel={() => {
          handleCancel();
          setSelectedModules([]);
          setPermissions({});
        }}
        centered
        bodyStyle={{
          maxHeight: 700,
          overflowY: "scroll",
          overflowX: "hidden",
        }}
        width={700}
        maskClosable={false}
        footer={[
          <Button key="back" onClick={handleCancel}>
            CANCEL
          </Button>,
          <Button
            type="primary"
            key="submit"
            style={{
              backgroundColor: "#073763",
              borderColor: "#073763",
              color: "white",
            }}
            form="userForm"
            loading={buttonLoading}
            htmlType="submit"
          >
            {formButton}
          </Button>,
        ]}
      >
        <Form
          id="userForm"
          form={UserForm}
          layout="vertical"
          onFinish={(values) => {
            try {
              handleSubmit(values);
            } catch (error) {
              showErrorMessage(error.message || "Something went wrong!");
            }
          }}
          autoComplete="off"
          initialValues={{
            role: UserForm.getFieldValue("role") || undefined,
          }}
        >
          <Row gutter={{ xs: 24, sm: 12, md: 24, lg: 24 }}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                label="Name"
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Please enter user name!",
                  },
                  {
                    pattern: /^[a-zA-Z\s]{3,}$/,
                    message:
                      "Name must contain only alphabets and at least 3 characters.",
                  },
                ]}
              >
                <Input disabled={editFlag} placeholder="Enter user name" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                label="Roles"
                name="role"
                rules={[
                  {
                    required: true,
                    message: "Please select user role!",
                  },
                ]}
              >
                <Select
                  placeholder="Select user role"
                  allowClear
                  showSearch
                  onChange={(value) => onChangeRolePermission(value)}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {roles
                    .filter((item) => item.role.toLowerCase() !== "super admin")
                    .map((item) => (
                      <Select.Option key={item.role} value={item.roleId}>
                        {item.role}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  {
                    required: true,
                    message: "Please enter user email!",
                  },
                  {
                    type: "email",
                    message: "Please input a valid email!",
                  },
                ]}
              >
                <Input disabled={editFlag} placeholder="Enter user email" />
              </Form.Item>
            </Col>

            {!editFlag && (
              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item
                  label="Password"
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: "Please input your password!",
                    },
                    {
                      pattern:
                        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{12,}$/,
                      message:
                        "Password must have at least 12 characters, including uppercase, lowercase, number, and special character.",
                    },
                  ]}
                >
                  <Input.Password
                    autoComplete="new-password"
                    placeholder="Enter strong password"
                  />
                </Form.Item>
              </Col>
            )}

            <Col xs={24}>
              <Form.Item
                label="Permissions"
                name="permissions"
                rules={[
                  {
                    required: true,
                    message: "Please select permissions!",
                  },
                ]}
              >
                <Checkbox.Group>
                  <Row gutter={[16, 16]}>
                    {Array.isArray(filteredModules) &&
                      filteredModules?.map((module) => (
                        <Col key={module?.moduleName} span={12}>
                          <Checkbox
                            disabled={!UserForm?.getFieldValue("role")}
                            value={module?.moduleName}
                            checked={
                              Array.isArray(selectedModules) &&
                              selectedModules.some(
                                (m) => m.module === module?.moduleName
                              )
                            }
                            onChange={() =>
                              handleModuleChangenew({
                                module: module?.moduleName,
                                key: module?.key,
                              })
                            }
                          >
                            {module?.moduleName}
                          </Checkbox>
                        </Col>
                      ))}
                  </Row>
                </Checkbox.Group>
              </Form.Item>
              <Collapse
                defaultActiveKey={selectedModules?.map((m) => m.module)}
              >
                {Array.isArray(selectedModules) &&
                  selectedModules.map((module, index) => (
                    <Panel header={module.module} key={module.module}>
                      <Form.Item
                        name={`${module.module.replace(
                          /\s+/g,
                          "_"
                        )}_permissions`}
                        rules={[
                          {
                            required: true,
                            message: "Please select at least one option!",
                          },
                        ]}
                      >
                        <Checkbox.Group
                          value={permissions[module.module] || []}
                          onChange={(selected) =>
                            handlePermissionChange(module.module, selected)
                          }
                        >
                          <Checkbox value="create">Allow Create</Checkbox>
                          <Checkbox value="read">Allow Read</Checkbox>
                          <Checkbox value="update">Allow Update</Checkbox>
                          <Checkbox value="delete">Allow Delete</Checkbox>
                        </Checkbox.Group>
                      </Form.Item>
                    </Panel>
                  ))}
              </Collapse>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

UserModal.propTypes = {
  fromTitle: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  buttonLoading: PropTypes.bool.isRequired,
  handleCancel: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  editFlag: PropTypes.bool.isRequired,
  formButton: PropTypes.string.isRequired,
  UserForm: PropTypes.object.isRequired,
  selectedModules: PropTypes.array.isRequired,
  handleModuleChangenew: PropTypes.func.isRequired,
  roles: PropTypes.array.isRequired,
  modules: PropTypes.array.isRequired,
};

export default UserModal;
